import { useState, useEffect } from 'react';
import type { Product } from '@/mocks/products';
import { supabase } from '@/lib/supabase';

interface InquiryModalProps {
  product: Product | null;
  onClose: () => void;
}

export default function InquiryModal({ product, onClose }: InquiryModalProps) {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);

  useEffect(() => {
    if (product) {
      document.body.style.overflow = 'hidden';
    }
    return () => { document.body.style.overflow = ''; };
  }, [product]);

  if (!product) return null;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus('submitting');

    const form = e.currentTarget;
    const data = new FormData(form);

    const contact_name = data.get('contact_name')?.toString() || '';
    const phone = data.get('phone')?.toString() || '';
    const email = data.get('email')?.toString() || '';
    const company = data.get('company')?.toString() || '';
    const order_quantity = data.get('order_quantity')?.toString() || '';
    const message = data.get('message')?.toString() || '';

    try {
      const { error } = await supabase.from('inquiries').insert({
        contact_name,
        phone,
        email,
        company: company || null,
        product_name: product.name,
        product_code: product.code,
        interest_category: product.category,
        order_quantity: order_quantity || null,
        message: message || '상품 문의',
        status: 'pending',
      });

      if (error) throw error;
      setStatus('success');
    } catch {
      setStatus('error');
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl animate-fadeIn">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">Product Inquiry</p>
            <h2 className="text-lg font-bold text-gray-900 mt-0.5">商品询价 <span className="text-sm font-normal text-gray-400">/ 상품 문의</span></h2>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-black hover:bg-gray-100 rounded-full transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {/* Product Info */}
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <img
              src={product.image}
              alt={product.nameZh}
              className="w-14 h-14 object-cover rounded-lg border border-gray-200"
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs text-gray-500 bg-gray-200 px-2 py-0.5 rounded">{product.category}</span>
                <span className="text-xs text-gray-400 font-mono">{product.code}</span>
              </div>
              <p className="font-semibold text-gray-900 text-sm mt-1 line-clamp-2">{product.nameZh}</p>
              <p className="text-xs text-gray-400 mt-0.5 tracking-widest">{product.name}</p>
              {product.boxQuantity > 0 && (
                <p className="text-xs text-gray-500 mt-0.5">
                  箱入数 {product.boxQuantity}个
                  {product.boxPrice > 0 && <span className="ml-2">· 箱价 {product.boxPrice}元</span>}
                </p>
              )}
            </div>
          </div>
        </div>

        {status === 'success' ? (
          <div className="px-6 py-12 text-center">
            <div className="w-16 h-16 flex items-center justify-center bg-black rounded-full mx-auto mb-4">
              <i className="ri-check-line text-white text-3xl"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">询价已提交</h3>
            <p className="text-sm text-gray-500 mb-1">工作人员将在1~2个工作日内与您联系。</p>
            <p className="text-xs text-gray-400 mb-6">담당자가 영업일 기준 1~2일 이내 연락드립니다.</p>
            <button
              onClick={onClose}
              className="px-8 py-2.5 bg-black text-white text-sm font-semibold rounded-md hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap"
            >
              确认 / 확인
            </button>
          </div>
        ) : (
          <form data-readdy-form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
            {/* 문의 상품 확인 영역 */}
            <div className="bg-gray-50 rounded-lg px-4 py-3 border border-gray-100">
              <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-2">询价商品 / 문의 상품</p>
              <div className="flex items-start gap-2">
                <div className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
                  <i className="ri-checkbox-circle-fill text-green-600 text-base"></i>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-800 leading-snug">{product.nameZh}</p>
                  <p className="text-xs text-gray-400 tracking-widest mt-0.5">{product.name}</p>
                  <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                    <span className="text-[11px] text-gray-500 bg-white border border-gray-200 px-2 py-0.5 rounded">{product.code}</span>
                    <span className="text-[11px] text-gray-500">{product.specificationZh}</span>
                    {product.boxQuantity > 0 && (
                      <span className="text-[11px] text-gray-500">箱入数 {product.boxQuantity}个</span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                  联系人 <span className="font-normal text-gray-400">/ 담당자명</span> <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="contact_name"
                  required
                  placeholder="张三 / 홍길동"
                  className="w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-black transition-colors"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                  联系电话 <span className="font-normal text-gray-400">/ 연락처</span> <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  name="phone"
                  required
                  placeholder="010-0000-0000"
                  className="w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-black transition-colors"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                邮箱 <span className="font-normal text-gray-400">/ 이메일</span> <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                name="email"
                required
                placeholder="example@company.com"
                className="w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-black transition-colors"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                公司名称·行业 <span className="font-normal text-gray-400">/ 회사명·업종</span>
              </label>
              <input
                type="text"
                name="company"
                placeholder="例: 网络销售 / 예: 온라인 판매"
                className="w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-black transition-colors"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                预计订购数量 <span className="font-normal text-gray-400">/ 예상 주문 수량</span>
              </label>
              <select
                name="order_quantity"
                className="w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:border-black transition-colors bg-white cursor-pointer"
              >
                <option value="">请选择 / 선택하세요</option>
                <option value="1~5箱">1~5箱 (少量) / 1~5박스 (소량)</option>
                <option value="6~20箱">6~20箱 / 6~20박스</option>
                <option value="21~50箱">21~50箱 / 21~50박스</option>
                <option value="51箱以上">51箱以上 (大量) / 51박스 이상 (대량)</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                询价内容 <span className="font-normal text-gray-400">/ 문의 내용</span>
              </label>
              <textarea
                name="message"
                rows={3}
                maxLength={500}
                placeholder="请填写感兴趣的商品、预计订购数量及交易条件等。/ 추가 문의 사항을 입력해 주세요."
                onChange={(e) => setCharCount(e.target.value.length)}
                className="w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-black transition-colors resize-none"
              />
              <p className="text-xs text-gray-400 text-right mt-1">{charCount}/500</p>
            </div>

            {status === 'error' && (
              <p className="text-xs text-red-500 bg-red-50 px-3 py-2 rounded-md">
                提交失败，请重试。/ 문의 접수 중 오류가 발생했습니다.
              </p>
            )}

            <button
              type="submit"
              disabled={status === 'submitting'}
              className="w-full py-3 bg-black text-white text-sm font-bold rounded-md hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
            >
              {status === 'submitting' ? '提交中... / 접수 중...' : '提交询价 / 문의 접수하기'}
            </button>
            <p className="text-xs text-gray-400 text-center">
              工作人员将在1~2个工作日内与您联系。<br />
              <span className="text-gray-300">영업일 기준 1~2일 이내 담당자가 연락드립니다.</span>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
